import React, { useState, useEffect, useContext, createContext } from 'react'
import { useLocalStorage } from 'react-use'

import { getLocationInfo } from 'src/api/getLocationInfo'

import { locationService } from 'src/utils/LocationService'

export type LocationInfo = {
  country: string | undefined
  state: string | undefined
  location: string | undefined
  isLoading: boolean
}

const defaults = {
  country: undefined,
  state: undefined,
  location: undefined,
  isLoading: true,
}

export const LocationContext = createContext<LocationInfo>(defaults)

const LOCATION_CACHE_TTL = 1000 * 60 * 60 * 24 // 1 day
const LOCATION_CACHE_KEY = 'asktia-preferred-location'

interface CachedLocation extends Omit<LocationInfo, 'isLoading'> {
  created: number
}

const isValidCachedLocation = (
  location: CachedLocation | undefined
): location is CachedLocation => Boolean(
  location &&
  location.country &&
  location.state &&
  location.location &&
  typeof location.created === 'number' &&
  Number.isFinite(location.created) &&
  location.created + LOCATION_CACHE_TTL > Date.now()
)

export const LocationProvider: React.FC<{ children: React.ReactNode[] }> = ({
  children
}) => {
  const [cachedLocation, setCachedLocation] = useLocalStorage<CachedLocation>(
    LOCATION_CACHE_KEY,
    undefined,
    {
      raw: false,
      serializer: JSON.stringify,
      deserializer: JSON.parse,
    }
  )

  const [locationInfo, setLocationInfo] = useState<LocationInfo>(() => {
    if (!isValidCachedLocation(cachedLocation)) return defaults

    return {
      country: cachedLocation.country,
      state: cachedLocation.state,
      location: cachedLocation.location,
      isLoading: false,
    }
  })

  useEffect(() => {
    if (isValidCachedLocation(cachedLocation)) return

    let isMounted = true

    const fetchLocationInfo = async () => {
      try {
        const { country, state } = await locationService.getLocation()

        if (!country || !state) {
          if (isMounted) {
            setLocationInfo({ country, state, location: undefined, isLoading: false })
          }
          return
        }

        if ('US'.localeCompare(country, 'en', { sensitivity: 'base' }) !== 0) {
          if (isMounted) {
            setLocationInfo({ country, state, location: undefined, isLoading: false })
          }
          return
        }

        let proposedState = null
        // non production environments allow to specify the target state for testing purposes
        if (process.env.GATSBY_ENVIRONMENT !== 'production') {
          const urlParams = new URLSearchParams(window.location.search)
          proposedState = urlParams.has('state') ? urlParams.get('state') : null
        }

        const result = { location: proposedState ? proposedState : state }

        /**
         * If the state is one of the multi clinic states, we need to get the location info
         */
        const targetState = ['NY', 'CA'].find(
          (item) => item.localeCompare(result.location, 'en', { sensitivity: 'base' }) === 0
        )
        if ('string' === typeof targetState) {
          const response = await getLocationInfo(proposedState)
          result.location = response.success ? response.data.location : result.location
        }

        /**
         * If the state is AZ, we need to set the location to Phoenix/Scottsdale
         */
        if ('AZ'.localeCompare(state, 'en', { sensitivity: 'base' }) === 0) {
          result.location = 'Phoenix/Scottsdale'
        }

        const location = result.location.toLowerCase()
        const resolvedLocation = { country, state, location, created: Date.now() }

        if (isMounted) {
          setLocationInfo({ country, state, location, isLoading: false })
          setCachedLocation(resolvedLocation)
        }
      } catch (error) {
        console.error('Error checking location:', error)

        if (isMounted) {
          setLocationInfo({ ...defaults, isLoading: false })
        }
      }
    }

    fetchLocationInfo()

    return () => {
      isMounted = false
    }
  }, [])

  return (
    <LocationContext.Provider value={locationInfo}>
      {children}
    </LocationContext.Provider>
  )
}

export const useLocation = () => {
  const { country, state, location, isLoading } = useContext(LocationContext)
  return { country, state, location, isLoading }
}
